import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/Branding/Logo';
import TextInput from '../../components/FormElements/TextInput';
import PasswordInput from '../../components/FormElements/PasswordInput';
import SelectInput from '../../components/FormElements/SelectInput';
import CheckboxInput from '../../components/FormElements/CheckboxInput';

import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';

import useCountryOptions from '../../hooks/useCountryOptions';

function RegisterSupplierScreen() {
    const countryOptions = useCountryOptions();
    const [formData, setFormData] = useState({
        fullName: '',
        companyName: '',
        phoneNumber: '',
        city: '',
        companyAddress: '',
        country: '',
        email: '',
        password: '',
        confirmPassword: '',
        privacyPolicy: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const handleChange = (e) => {
        setError(null); // Clear error on new input
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (formData.password !== formData.confirmPassword) {
            alert("Las contraseñas no coinciden.");
            return;
        }
        if (!formData.privacyPolicy) {
            alert("Debes aceptar las políticas de privacidad para continuar.");
            return;
        }
        if (!formData.country) {
            alert("Por favor, selecciona tu país de origen.");
            return;
        }

        // Basic phone validation
        if (!formData.phoneNumber || formData.phoneNumber.replace(/\D/g, '').length < 8) {
            alert("Por favor, ingresa un número de WhatsApp válido.");
            return;
        }
        if (!formData.city) {
            alert("Por favor, ingresa tu ciudad.");
            return;
        }
        if (!formData.fullName) {
            alert("Por favor, ingresa tu nombre completo.");
            return;
        }

        const payload = {
            username: formData.fullName,
            email: formData.email,
            password: formData.password,
            country: formData.country,
            birth_date: '1990-01-01',
            companyName: formData.companyName,
            phoneNumber: formData.phoneNumber,
            city: formData.city,
            companyEmail: '', // always blank on registration
            companyAddress: '', // always blank on registration
            initial_form_json: null
        };

        setIsSubmitting(true);
        console.log('Registering supplier with payload:', payload);

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

        try {
            const response = await fetch(`${API_BASE_URL}/api/suppliers/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = data.error?.message || data.message || `Registration failed with status: ${response.status}`;
                setError(errorMessage);
                throw new Error(errorMessage);
            }

            console.log('Supplier registration successful:', data);
            alert('¡Registro de proveedor exitoso! Serás redirigido al inicio de sesión.');
            navigate('/login');
        } catch (err) {
            console.error('Supplier registration error:', err.message);
            if (!error) setError(err.message || 'Ocurrió un error durante el registro. Por favor, inténtalo de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="col-md-9 form-side m-auto">
            <div className="form-container text-center">
                <Logo />
                <h3 className="fw-bold mb-4">Registro Proveedor</h3>
                <p className="text-muted mb-4">Por favor, completa el siguiente formulario para crear una cuenta de proveedor.</p>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                <form onSubmit={handleSubmit} className="checkin-form text-start" noValidate>
                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Nombre completo"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Nombre de tu emprendimiento o empresa"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                    </div>
                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Email"
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                iconClass="bi bi-envelope"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="phoneNumber" className="form-label">Número de WhatsApp</label>
                            <PhoneInput
                                country={'cl'}
                                value={formData.phoneNumber}
                                onChange={phone => setFormData(prev => ({ ...prev, phoneNumber: phone }))}
                                inputProps={{
                                    name: 'phoneNumber',
                                    id: 'phoneNumber',
                                    required: true,
                                    disabled: isSubmitting,
                                    className: 'form-control'
                                }}
                                inputStyle={{ width: '100%' }}
                                specialLabel=""
                                enableSearch
                                disableDropdown={false}
                            />
                        </div>
                    </div>
                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <SelectInput
                                label="País"
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                defaultOptionText="Selecciona tu país..."
                                options={countryOptions}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Ciudad"
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                    </div>

                    {/* Password and confirm password row remains unchanged */}

                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <PasswordInput
                                label="Contraseña"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <PasswordInput
                                label="Confirmar contraseña"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                    </div>

                    <div className="my-4">
                        <CheckboxInput
                            id="privacyPolicy"
                            name="privacyPolicy"
                            checked={formData.privacyPolicy}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            required
                            label={<>Confirmo que he leído y acepto las <a href="#">políticas de privacidad</a> y <a href="#">términos de servicio para proveedores</a> de Checkin.</>}
                        />
                    </div>

                    <div className="buttons-container d-grid gap-3 mt-5 mb-3">
                        <button type="submit" className="btn btn-checkin" disabled={isSubmitting}>
                            {isSubmitting ? 'Registrando...' : 'Registrar Proveedor'}
                        </button>
                    </div>
                </form>
                <p className="mt-3">¿Ya tienes una cuenta? <Link to="/login">Inicia sesión aquí</Link></p>
            </div>
        </div>
    );
}

export default RegisterSupplierScreen;